import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Box, Button, Stack, Typography } from '@mui/material'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

import StandaloneCardLayout from '../../components/layouts/StandaloneCardLayout'
import ReleaseCard from '../../components/release/ReleaseCard'
import { useAuth } from '../../contexts/AuthContext'
import { getReleases, type Release } from '../../utils/releases'
import { generateFullUrl } from '../../utils/urls'
import { useNotify } from '../../components/shared/ToastProvider'

const HIGHLIGHT_DURATION_MS = 1800

const ReleaseHistory: React.FC = () => {
  const navigate = useNavigate()
  const { showSuccess, showError } = useNotify()
  const { user, isAuthenticated, loading: authLoading } = useAuth()
  const releases = useMemo(() => getReleases(user?.role), [user?.role])
  const latestVersion = releases[0]?.version ?? null

  const [expandedVersion, setExpandedVersion] = useState<string | null>(null)
  const [highlightedVersion, setHighlightedVersion] = useState<string | null>(null)

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      window.location.href = '/login'
    }
  }, [authLoading, isAuthenticated])

  useEffect(() => {
    if (!releases.length || typeof window === 'undefined') return

    const timers: number[] = []

    const applyHash = () => {
      const hash = window.location.hash.replace(/^#/, '')
      if (!hash) return
      const version = hash.startsWith('v') ? hash.slice(1) : hash
      if (!releases.some((r) => r.version === version)) return
      setExpandedVersion(version)
      setHighlightedVersion(version)
      timers.push(
        window.setTimeout(() => {
          document.getElementById(`v${version}`)?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          })
        }, 50),
      )
      timers.push(
        window.setTimeout(
          () => setHighlightedVersion(null),
          HIGHLIGHT_DURATION_MS,
        ),
      )
    }

    applyHash()
    window.addEventListener('hashchange', applyHash)
    return () => {
      window.removeEventListener('hashchange', applyHash)
      timers.forEach((id) => window.clearTimeout(id))
    }
  }, [releases])

  const handleToggle = useCallback((version: string) => {
    setExpandedVersion((current) => (current === version ? null : version))
  }, [])

  const handleCopyLink = useCallback(async (release: Release) => {
    const url = generateFullUrl(`/releases#v${release.version}`)
    try {
      await navigator.clipboard.writeText(url)
      showSuccess('Release link copied')
    } catch {
      showError('Failed to copy link')
    }
  }, [showSuccess, showError])

  if (authLoading || !isAuthenticated) {
    return (
      <StandaloneCardLayout headerTitle="What's New" containerMaxWidth={1000}>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body2" color="textSecondary">
            Loading...
          </Typography>
        </Box>
      </StandaloneCardLayout>
    )
  }

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1)
    } else {
      navigate('/')
    }
  }

  return (
    <>
      <Helmet>
        <title>What&apos;s New | Inspector Tool</title>
      </Helmet>
      <StandaloneCardLayout headerTitle="What's New" containerMaxWidth={1000}>
        <Box
          sx={{
            width: '100%',
            maxWidth: '100%',
            minWidth: 0,
            boxSizing: 'border-box',
          }}
        >
          {releases.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="textSecondary">
                No release notes are available yet.
              </Typography>
            </Box>
          ) : (
            <Stack spacing={1.5} useFlexGap>
              {releases.map((release) => {
                const isLatest = release.version === latestVersion
                return (
                  <ReleaseCard
                    key={release.version}
                    release={release}
                    anchorId={`v${release.version}`}
                    expanded={expandedVersion === release.version}
                    onToggle={handleToggle}
                    isLatest={isLatest}
                    isHighlighted={highlightedVersion === release.version}
                    onCopyLink={handleCopyLink}
                  />
                )
              })}
            </Stack>
          )}

          <Box
            sx={{
              mt: 4,
              pt: 2,
              borderTop: '1px solid rgba(0,0,0,0.08)',
              display: 'flex',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Button startIcon={<ArrowBackIcon />} onClick={goBack} size="small">
              Back
            </Button>
            <Button component={RouterLink} to="/" size="small">
              Go to dashboard
            </Button>
          </Box>
        </Box>
      </StandaloneCardLayout>
    </>
  )
}

export default ReleaseHistory
